// src/components/Control/Operator/Snapshots.tsx
// Снимки с камер: затвор на окне камеры (главный экран), настройки (куда
// сохранять, набор, серия) и галерея снимков на роботе с выгрузкой части или
// всех в ZIP (папки по наборам + snapshots.json / snapshots.csv — готовая
// разметка для обучения распознавания) и удалением. Логика съёмки —
// robot/snapshots.ts, хранение на роботе — нода snapshot (snapshot_store.py):
//   robot/<id>/snap/index        (get) -> {"items": [SnapItem, ...]}
//   robot/<id>/snap/file/<name>  (get) -> JPEG;  snap/thumb/<name> -> превью
//   robot/<id>/snap/cmd          <- {"cmd": "delete", "names"} | {"cmd": "delete_all"} | {"cmd": "label", "names", "label"}
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as zenoh from '@eclipse-zenoh/zenoh-ts';
import { CamId, useRobot, useSnapshotsState, useTelemetryState } from '../robot/RobotContext';
import { LABEL_MAX, SnapDest, SnapItem, downloadBlob, safeName, stamp } from '../robot/snapshots';
import { STALE, fresh } from '../robot/telemetry';
import { getBytes, putJson } from '../../../hooks/useZenohSubscription';
import { useTick } from '../../../hooks/useTick';
import { ZipWriter } from '../../../utils/zip';
import { Icon, Segmented } from './ui';

const NO_REPLY = 'робот не ответил (нода snapshot не запущена?)';
const PAGE = 60;
const NO_LABEL = 'без_набора';

const mb = (b: number | null | undefined) =>
  b == null ? '—' : b >= 1e9 ? `${(b / 1e9).toFixed(1)} ГБ` : b >= 1e6 ? `${(b / 1e6).toFixed(b < 1e7 ? 1 : 0)} МБ` : `${Math.max(1, Math.round(b / 1e3))} КБ`;
const camName = (cam: string) => `камера ${Number(cam) + 1}`;
const when = (t: number) => new Date(t).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' });

// --- затвор на окне камеры ----------------------------------------------------------------

export const Shutter: React.FC<{ cam: CamId; className?: string }> = ({ cam, className }) => {
  const { snapshots } = useRobot();
  const { series, last, busy, dest, label, count } = useSnapshotsState(s => ({
    series: s.series, last: s.last, busy: s.busy, dest: s.dest, label: s.label, count: s.seriesCount,
  }));
  const [, rerender] = useState(0);
  const lastAt = last?.cam === cam ? last.at : 0;
  // Подпись и вспышка гаснут сами
  useEffect(() => {
    if (!lastAt) return;
    const id = setTimeout(() => rerender(n => n + 1), 2600);
    return () => clearTimeout(id);
  }, [lastAt]);
  const recent = last && last.cam === cam && Date.now() - last.at < 2500 ? last : null;
  const mine = series?.cam === cam ? series : null;
  const other = !!series && !mine;
  const where = dest === 'robot' ? 'на робот' : 'в браузер';
  const title = mine
    ? `Остановить серию (${mine.done} из ${mine.total})`
    : `Снимок ${camName(cam)} ${where}${label ? `, набор «${label}»` : ''}${count > 1 ? ` — серия ${count}` : ''}`;
  return (
    <div className={`op-shutter-wrap ${className ?? ''}`} onClick={e => e.stopPropagation()}
         onPointerDown={e => e.stopPropagation()} onPointerUp={e => e.stopPropagation()}>
      {recent?.ok && <div key={recent.at} className="op-shutter-flash" aria-hidden="true" />}
      <button type="button" className={`op-shutter ${mine ? 'series' : ''} ${busy && !mine ? 'busy' : ''}`}
              aria-label={title} title={title} disabled={other} onClick={() => snapshots.shoot(cam)}>
        {mine ? <span className="op-shutter-count">{mine.done}/{mine.total}</span> : <Icon name="photo" size={20} />}
      </button>
      {recent && (
        <span className={`op-shutter-note ${recent.ok ? '' : 'bad'}`} role="status">{recent.text}</span>
      )}
    </div>
  );
};

// --- настройки ----------------------------------------------------------------------------

export const SnapshotSettings: React.FC = () => {
  const { snapshots } = useRobot();
  const st = useSnapshotsState(s => ({ dest: s.dest, label: s.label, seriesCount: s.seriesCount, seriesInterval: s.seriesInterval, taken: s.taken }));
  const labels = useTelemetryState(t => t.snap?.labels ?? null);
  const known = useMemo(() => Object.keys(labels ?? {}).filter(Boolean).sort(), [labels]);
  const [labelDraft, setLabelDraft] = useState<string | null>(null);
  const commitLabel = () => {
    if (labelDraft !== null) snapshots.configure({ label: labelDraft.trim().slice(0, LABEL_MAX) });
    setLabelDraft(null);
  };
  return (
    <div className="feature-card">
      <h3><span>Съёмка</span>{st.taken > 0 && <span className="badge ok">снято сейчас: {st.taken}</span>}</h3>
      <div className="row">
        <Segmented<SnapDest> label="Куда сохранять снимки" value={st.dest} onChange={dest => snapshots.configure({ dest })}
                             options={[['robot', 'На робот'], ['client', 'В браузер (скачать)']]} />
      </div>
      <p className="hint">
        {st.dest === 'robot'
          ? 'Робот сохраняет свой последний кадр камеры в полном качестве (не зависит от FPS передачи) вместе с позой робота и углами поворотной камеры. Выгрузить их можно ниже — выбранные или все, архивом ZIP.'
          : 'Скачивается кадр, который сейчас на экране, файлом в папку загрузок браузера. Для серии браузер может один раз спросить разрешение на загрузку нескольких файлов.'}
      </p>
      <div className="op-num-grid">
        <label className="op-num">
          <span>Набор (метка)</span>
          <span className="op-num-input">
            <input type="text" list="snap-labels" value={labelDraft ?? st.label} maxLength={LABEL_MAX} placeholder="напр. зарядка"
                   onChange={e => setLabelDraft(e.target.value)} onBlur={commitLabel}
                   onKeyDown={e => { if (e.key === 'Enter') commitLabel(); }} />
          </span>
        </label>
        <label className="op-num">
          <span>Снимков за нажатие</span>
          <span className="op-num-input">
            <input type="number" min={1} max={500} value={st.seriesCount}
                   onChange={e => snapshots.configure({ seriesCount: Math.max(1, Math.min(500, Math.round(Number(e.target.value) || 1))) })} />
          </span>
        </label>
        <label className="op-num">
          <span>Интервал серии</span>
          <span className="op-num-input">
            <input type="number" min={0.5} max={60} step={0.5} value={st.seriesInterval} disabled={st.seriesCount <= 1}
                   onChange={e => snapshots.configure({ seriesInterval: Math.max(0.5, Math.min(60, Number(e.target.value) || 2)) })} />
            <small>с</small>
          </span>
        </label>
      </div>
      <datalist id="snap-labels">{known.map(l => <option key={l} value={l} />)}</datalist>
      <p className="hint">
        Кнопка снимка <Icon name="photo" size={14} /> — в левом нижнем углу окна камеры на главном экране (у каждой камеры своя).
        Набор задаёт папку в архиве: для обучения распознавания снимайте объект (например, зарядную станцию) с разных
        расстояний, углов и при разном свете в один набор, а «фон» без объекта — в другой. Серия снимает N кадров
        через интервал, пока открыта страница; повторное нажатие — стоп.
      </p>
    </div>
  );
};

// --- галерея на роботе ---------------------------------------------------------------------

// Превью грузятся по мере появления на экране, не больше 4 запросов сразу
class ThumbLoader {
  urls = new Map<string, string>();
  private queue: [string, () => void][] = [];
  private active = 0;
  constructor(private session: zenoh.Session, private prefix: string) {}

  load(name: string, done: () => void) {
    if (this.urls.has(name)) return done();
    this.queue.push([name, done]);
    this.pump();
  }

  private pump() {
    while (this.active < 4 && this.queue.length) {
      const [name, done] = this.queue.shift()!;
      this.active++;
      getBytes(this.session, `${this.prefix}/snap/thumb/${name}`, NO_REPLY)
        .then(b => { this.urls.set(name, URL.createObjectURL(new Blob([b as BlobPart], { type: 'image/jpeg' }))); })
        .catch(() => { this.urls.set(name, ''); })
        .finally(() => { this.active--; done(); this.pump(); });
    }
  }

  dispose() {
    this.queue = [];
    this.urls.forEach(u => u && URL.revokeObjectURL(u));
    this.urls.clear();
  }
}

const Thumb: React.FC<{ item: SnapItem; loader: ThumbLoader | null; selected: boolean; onToggle: () => void; onOpen: () => void }> = ({ item, loader, selected, onToggle, onOpen }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [url, setUrl] = useState<string | null>(loader?.urls.get(item.name) ?? null);
  useEffect(() => {
    if (!loader || url !== null || !ref.current) return;
    const el = ref.current;
    const io = new IntersectionObserver(entries => {
      if (!entries.some(e => e.isIntersecting)) return;
      io.disconnect();
      loader.load(item.name, () => setUrl(loader.urls.get(item.name) ?? ''));
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [loader, item.name, url]);
  return (
    <div ref={ref} className={`op-snap-tile ${selected ? 'on' : ''}`}>
      <button type="button" className="op-snap-img" onClick={onOpen} aria-label={`Открыть снимок ${item.name}`}>
        {url ? <img src={url} alt="" loading="lazy" /> : <span className="op-muted">{url === '' ? 'нет превью' : '…'}</span>}
      </button>
      <label className="op-snap-check">
        <input type="checkbox" checked={selected} onChange={onToggle} aria-label={`Выбрать ${item.name}`} />
      </label>
      <div className="op-snap-caption">
        <span>{when(item.t)}</span>
        <span>{item.label || '—'} · к{Number(item.cam) + 1}</span>
      </div>
    </div>
  );
};

function csvOf(items: SnapItem[], fileOf: (m: SnapItem) => string) {
  const esc = (v: unknown) => (v == null ? '' : /[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v));
  const rows = [['file', 'label', 'camera', 'time', 'width', 'height', 'x', 'y', 'yaw', 'pan', 'tilt'].join(',')];
  for (const m of items) {
    rows.push([fileOf(m), m.label, Number(m.cam) + 1, new Date(m.t).toISOString(), m.w, m.h,
               m.pose?.x, m.pose?.y, m.pose?.yaw, m.pan_tilt?.pan, m.pan_tilt?.tilt].map(esc).join(','));
  }
  return rows.join('\n') + '\n';
}

export const SnapshotGallery: React.FC = () => {
  useTick(5000);
  const { session, robotId, notify } = useRobot();
  const prefix = `robot/${robotId}`;
  const { snap, snapAt } = useTelemetryState(t => ({ snap: t.snap, snapAt: t.snapAt }));
  const state = snap && fresh(snapAt, STALE.node) ? snap : null;
  const [items, setItems] = useState<SnapItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState<string>('*');
  const [cam, setCam] = useState<'all' | CamId>('all');
  const [shown, setShown] = useState(PAGE);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState<number | null>(null);
  const [job, setJob] = useState<{ done: number; total: number; bytes: number } | null>(null);
  const [exported, setExported] = useState<string[] | null>(null);
  const [newLabel, setNewLabel] = useState('');
  const cancel = useRef(false);

  const loader = useMemo(() => (session ? new ThumbLoader(session, prefix) : null), [session, prefix]);
  useEffect(() => () => loader?.dispose(), [loader]);

  const load = useCallback(() => {
    if (!session) return;
    getBytes(session, `${prefix}/snap/index`, NO_REPLY)
      .then(b => {
        const msg = JSON.parse(new TextDecoder().decode(b)) as { items: SnapItem[] };
        const list = (msg.items ?? []).slice().sort((a, c) => c.t - a.t);   // новые сверху
        setItems(list);
        setError(null);
        const names = new Set(list.map(m => m.name));
        setSelected(sel => new Set(Array.from(sel).filter(n => names.has(n))));
      })
      .catch(e => setError(e instanceof Error ? e.message : String(e)));
  }, [session, prefix]);
  // Новые снимки, удаление, смена набора — робот присылает новое состояние: перечитываем список
  const version = state ? `${state.count}/${state.newest}/${JSON.stringify(state.labels)}` : '';
  useEffect(() => { load(); }, [load, version]);

  const filtered = useMemo(() => (items ?? []).filter(m => (label === '*' || m.label === label) && (cam === 'all' || m.cam === cam)), [items, label, cam]);
  const labels = useMemo(() => {
    const m = new Map<string, number>();
    (items ?? []).forEach(i => m.set(i.label, (m.get(i.label) ?? 0) + 1));
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [items]);
  const allSelected = filtered.length > 0 && filtered.every(m => selected.has(m.name));
  const chosen = (items ?? []).filter(m => selected.has(m.name));

  const toggle = (name: string) => setSelected(sel => {
    const next = new Set(sel);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    return next;
  });
  const toggleAll = () => setSelected(sel => {
    const next = new Set(sel);
    filtered.forEach(m => (allSelected ? next.delete(m.name) : next.add(m.name)));
    return next;
  });

  const exportZip = async (list: SnapItem[]) => {
    if (!session || !list.length) return;
    cancel.current = false;
    setExported(null);
    const zip = new ZipWriter();
    const fileOf = (m: SnapItem) => `${safeName(m.label) || NO_LABEL}/${m.name}.jpg`;
    const ordered = list.slice().sort((a, b) => a.t - b.t);
    const done: SnapItem[] = [];
    const failed: string[] = [];
    setJob({ done: 0, total: ordered.length, bytes: 0 });
    for (const m of ordered) {
      if (cancel.current) break;
      try {
        const data = await getBytes(session, `${prefix}/snap/file/${m.name}`, NO_REPLY);
        zip.add(fileOf(m), data, new Date(m.t));
        done.push(m);
      } catch {
        failed.push(m.name);
      }
      setJob({ done: done.length + failed.length, total: ordered.length, bytes: zip.size });
    }
    setJob(null);
    if (cancel.current) {
      notify('Выгрузка отменена', 'info');
      return;
    }
    if (!done.length) {
      notify('Ни одного снимка не удалось получить с робота', 'error');
      return;
    }
    zip.add('snapshots.json', JSON.stringify(done.map(m => ({ ...m, file: fileOf(m) })), null, 1));
    zip.add('snapshots.csv', csvOf(done, fileOf));
    downloadBlob(zip.blob(), `${safeName(robotId)}-snapshots-${stamp(Date.now())}.zip`);
    setExported(done.map(m => m.name));
    notify(`Выгружено снимков: ${done.length}${failed.length ? `, не получено: ${failed.length}` : ''}`, failed.length ? 'warning' : 'success');
  };

  const remove = (names: string[], what: string) => {
    if (!names.length || !window.confirm(`Удалить с робота ${what}? Отменить нельзя.`)) return;
    putJson(session, `${prefix}/snap/cmd`, { cmd: 'delete', names })
      .then(ok => {
        notify(ok ? `Удаляю снимков: ${names.length}` : 'Не удалось отправить команду', ok ? 'info' : 'error');
        if (ok) {
          setSelected(sel => new Set(Array.from(sel).filter(n => !names.includes(n))));
          setOpen(null);
          setExported(null);
          setTimeout(load, 800);
        }
      });
  };
  const relabel = () => {
    const names = chosen.map(m => m.name);
    if (!names.length) return;
    putJson(session, `${prefix}/snap/cmd`, { cmd: 'label', names, label: newLabel.trim() })
      .then(ok => { notify(ok ? `Набор «${newLabel.trim() || 'без набора'}» — снимков: ${names.length}` : 'Не удалось отправить команду', ok ? 'success' : 'error'); setTimeout(load, 800); });
  };

  const openItem = open !== null ? filtered[open] ?? null : null;
  const busy = job !== null;

  return (
    <div className="feature-card op-snap-gallery">
      <h3>
        <span>Снимки на роботе</span>
        <span className="op-row">
          {state && <span className="badge off">{state.count} · {mb(state.used_bytes)} из {mb(state.max_bytes)}</span>}
          <button type="button" className="op-btn" onClick={load} disabled={!session}>Обновить</button>
        </span>
      </h3>
      {!state && <p className="hint">Нода snapshot на роботе не отвечает (не запущена или старая версия ПО на Pi).</p>}
      {state?.error && <p className="error-text">Последний снимок: {state.error}</p>}
      {error && <p className="error-text">{error}</p>}
      {state && state.free_bytes != null && state.free_bytes < 2e9 && <p className="hint">Свободно на диске робота: {mb(state.free_bytes)}.</p>}

      <div className="op-row op-snap-filters">
        <select aria-label="Набор" value={label} onChange={e => { setLabel(e.target.value); setShown(PAGE); setOpen(null); }}>
          <option value="*">Все наборы ({items?.length ?? 0})</option>
          {labels.map(([l, n]) => <option key={l} value={l}>{l || 'без набора'} ({n})</option>)}
        </select>
        <Segmented<'all' | CamId> label="Камера" value={cam} onChange={v => { setCam(v); setShown(PAGE); setOpen(null); }}
                                  options={[['all', 'Все'], ['0', 'Камера 1'], ['1', 'Камера 2']]} />
        <label className="op-check">
          <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={!filtered.length} />
          Выбрать все ({filtered.length})
        </label>
      </div>

      <div className="op-row op-snap-actions">
        <button type="button" className="op-btn primary" disabled={busy || !chosen.length} onClick={() => exportZip(chosen)}>
          Скачать выбранные ({chosen.length})
        </button>
        <button type="button" className="op-btn" disabled={busy || !filtered.length} onClick={() => exportZip(filtered)}>
          Скачать {label === '*' && cam === 'all' ? 'все' : 'показанные'} ({filtered.length})
        </button>
        <button type="button" className="op-btn danger" disabled={busy || !chosen.length}
                onClick={() => remove(chosen.map(m => m.name), `выбранные снимки (${chosen.length})`)}>
          Удалить выбранные
        </button>
      </div>
      {chosen.length > 0 && (
        <div className="op-row op-snap-actions">
          <input type="text" list="snap-labels-gallery" value={newLabel} maxLength={LABEL_MAX} placeholder="набор"
                 aria-label="Новый набор для выбранных" onChange={e => setNewLabel(e.target.value)} />
          <datalist id="snap-labels-gallery">{labels.filter(([l]) => l).map(([l]) => <option key={l} value={l} />)}</datalist>
          <button type="button" className="op-btn" onClick={relabel}>Перенести выбранные в набор</button>
          <button type="button" className="op-btn" onClick={() => setSelected(new Set())}>Снять выбор</button>
        </div>
      )}
      {job && (
        <div className="op-row op-snap-progress" role="status">
          <progress max={job.total} value={job.done} />
          <span>{job.done} из {job.total} · {mb(job.bytes)}</span>
          <button type="button" className="op-btn" onClick={() => { cancel.current = true; }}>Отмена</button>
        </div>
      )}
      {exported && (
        <p className="hint op-row">
          Архив сохранён ({exported.length} сн.).
          <button type="button" className="op-btn danger" onClick={() => remove(exported, `выгруженные снимки (${exported.length})`)}>
            Удалить выгруженные с робота
          </button>
        </p>
      )}

      {items && !filtered.length && (
        <p className="hint">{items.length ? 'Под этот фильтр снимков нет.' : 'Снимков на роботе нет — нажмите кнопку снимка на окне камеры (сохранение «На робот»).'}</p>
      )}
      <div className="op-snap-grid">
        {filtered.slice(0, shown).map((m, i) => (
          <Thumb key={m.name} item={m} loader={loader} selected={selected.has(m.name)}
                 onToggle={() => toggle(m.name)} onOpen={() => setOpen(i)} />
        ))}
      </div>
      {filtered.length > shown && (
        <button type="button" className="op-btn" onClick={() => setShown(n => n + PAGE)}>
          Показать ещё ({filtered.length - shown})
        </button>
      )}
      <p className="hint">
        В архиве снимки разложены по папкам наборов; snapshots.csv и snapshots.json — время, камера, размер кадра,
        поза робота на карте (x, y, yaw) и углы поворотной камеры на момент снимка. Робот сам снимки не удаляет:
        при заполнении ({mb(state?.max_bytes)}) новые не сохраняются — выгрузите и удалите старые.
      </p>
      {openItem && session && (
        <SnapViewer item={openItem} session={session} prefix={prefix}
                    onClose={() => setOpen(null)}
                    onPrev={open! > 0 ? () => setOpen(open! - 1) : undefined}
                    onNext={open! < filtered.length - 1 ? () => { setOpen(open! + 1); if (open! + 1 >= shown) setShown(n => n + PAGE); } : undefined}
                    onDelete={() => remove([openItem.name], 'этот снимок')} />
      )}
      {state && (
        <details>
          <summary>Удалить все снимки</summary>
          <button type="button" className="op-btn danger" disabled={busy || !state.count}
                  onClick={() => {
                    if (!window.confirm(`Удалить с робота все снимки (${state.count})? Отменить нельзя.`)) return;
                    putJson(session, `${prefix}/snap/cmd`, { cmd: 'delete_all' }).then(() => setTimeout(load, 800));
                  }}>
            Удалить все снимки на роботе
          </button>
        </details>
      )}
    </div>
  );
};

const SnapViewer: React.FC<{ item: SnapItem; session: zenoh.Session; prefix: string; onClose: () => void; onPrev?: () => void; onNext?: () => void; onDelete: () => void }> = ({ item, session, prefix, onClose, onPrev, onNext, onDelete }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    let made: string | null = null;
    setUrl(null);
    setError(null);
    getBytes(session, `${prefix}/snap/file/${item.name}`, NO_REPLY)
      .then(b => {
        made = URL.createObjectURL(new Blob([b as BlobPart], { type: 'image/jpeg' }));
        if (alive) setUrl(made);
        else URL.revokeObjectURL(made);
      })
      .catch(e => alive && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [session, prefix, item.name]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && onPrev) onPrev();
      if (e.key === 'ArrowRight' && onNext) onNext();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, onPrev, onNext]);
  return (
    <div className="op-snap-viewer" role="dialog" aria-label={`Снимок ${item.name}`} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="op-snap-viewer-body">
        <div className="op-snap-viewer-img">
          {url ? <img src={url} alt={`Снимок ${item.name}`} /> : <span className="op-cam-msg">{error ?? 'Загрузка…'}</span>}
        </div>
        <div className="op-snap-viewer-info">
          <strong>{when(item.t)}</strong>
          <span>{camName(item.cam)} · {item.w}×{item.h} · {mb(item.bytes)}</span>
          <span>Набор: {item.label || 'без набора'}</span>
          {item.pose && <span>Поза: x {item.pose.x.toFixed(2)}, y {item.pose.y.toFixed(2)} м, {(item.pose.yaw * 180 / Math.PI).toFixed(0)}°</span>}
          {item.pan_tilt && <span>Поворот камеры: {item.pan_tilt.pan.toFixed(0)}° / наклон {item.pan_tilt.tilt.toFixed(0)}°</span>}
        </div>
        <div className="op-row">
          <button type="button" className="op-btn" onClick={onPrev} disabled={!onPrev} aria-label="Предыдущий (новее)">←</button>
          <button type="button" className="op-btn" onClick={onNext} disabled={!onNext} aria-label="Следующий (старее)">→</button>
          {url && <a className="op-btn" href={url} download={`${item.name}${item.label ? `-${safeName(item.label)}` : ''}.jpg`}>Скачать</a>}
          <button type="button" className="op-btn danger" onClick={onDelete}>Удалить</button>
          <button type="button" className="op-btn primary" onClick={onClose}>Закрыть</button>
        </div>
      </div>
    </div>
  );
};
